import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import { ROUTE_PATH } from "utils/constants";
import { MISSION_GOAL, findOptionByValue, missionTypeFilterOptions, competitionFormatFilterOptions, missionGoalOptions, MISSION_STATUS } from "utils/constants/options";
import { useMasterData } from "utils/hooks";
import { adminService } from "apiServices/apiController/adminService";
import { getTargetUnitLabel } from "utils/functions/companyMissionRender";
import { calculatePercent } from "utils/functions/number";

import {
  BGLoading,
  BoxDeleteConfirm,
  Displays,
  Forms,
  Icons,
  Loading,
  Modal,
} from "components";
import { AddEditCompanyMissionV2ContainerStyled } from "./styled";
import { toast } from "react-toastify";

const AddEditCompanyMissionV2Container = () => {
  const { id } = useParams();
  const history = useHistory();
  const isCreate = useMemo(() => id === "create", [id]);
  const { companyOptions, loadingMasterData } = useMasterData({ fetchCompany: true });
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [isBgLoading, setIsBgLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [isShowModal, setIsShowModal] = useState(false);
  const [progressData, setProgressData] = useState(null);

  const handleBack = useCallback(() => {
    history.push(ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2);
  }, [history]);

  const handleTeamManagement = useCallback(() => {
    history.push(
      ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2_TEAM.replace(":id", id)
    );
  }, [history, id]);

  const handleLeaderboard = useCallback(() => {
    history.push(
      ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2_LEADERBOARD.replace(":id", id)
    );
  }, [history, id]);

  const fetchData = useCallback(async () => {
    if (loadingMasterData) return;
    setIsLoading(true);
    const res = await adminService.GET_MISSION_V2_BY_ID(id);
    if (res?.data) {
      const data = res.data;
      const missionGoal = data.mission_goal ?? "";
      const coins = Array.isArray(data.rank_coins) ? data.rank_coins : [];
      const coinForRank = (rank) => {
        return String(coins.find((c) => Number(c?.reward_rank) === rank)?.coin_amount ?? "");
      }
      const isSharedOrFirst =
        missionGoal === MISSION_GOAL.SHARED_GOAL.value ||
        missionGoal === MISSION_GOAL.FIRST_COMPLETE.value;
      const isHighest = missionGoal === MISSION_GOAL.HIGHEST_SCORE.value;
      const isTeamType = data.challenge_type === "team";

      setInitialValues({
        id: data.id ?? "",
        name_th: data.name_th ?? "",
        name_en: data.name_en ?? "",
        category: findOptionByValue(missionTypeFilterOptions, data.category),
        challenge_type: findOptionByValue(competitionFormatFilterOptions, data.challenge_type),
        company_ids: (data.companies || []).map((c) => {
          const match = companyOptions.find((opt) => opt.value === c.id);
          return { label: match?.label ?? c.name, value: c.id };
        }),
        begin_date: data.begin_date ? moment(data.begin_date).toDate() : null,
        end_date: data.end_date ? moment(data.end_date).toDate() : null,
        mission_goal: findOptionByValue(missionGoalOptions, missionGoal),
        target_amount: data.target_amount != null ? data.target_amount : "",
        success_coin_amount: isSharedOrFirst ? coinForRank(1) : "",
        fail_coin_amount: isSharedOrFirst ? coinForRank(2) : "",
        reward_1st_amount: isHighest ? coinForRank(1) : "",
        reward_2nd_amount: isHighest ? coinForRank(2) : "",
        reward_3rd_amount: isHighest ? coinForRank(3) : "",
        reward_consolation_amount: isHighest ? coinForRank(4) : "",
        image_file_th: data.image_uri_th ? [{ image_uri: data.image_uri_th }] : [],
        image_file_en: data.image_uri_en ? [{ image_uri: data.image_uri_en }] : [],
        registration_start_date: isTeamType && data.registration_date ? moment(data.registration_date).toDate() : null,
        register_end_date: isTeamType && data.end_date ? moment(data.end_date).toDate() : null,
        team_count: isTeamType && data.team_count != null ? String(data.team_count) : "",
        team_size: isTeamType && data.team_size != null ? String(data.team_size) : "",
        registered_teams: data?.joined_team ?? 0,
        registered_members: data.stats?.participants?.joined_count ?? 0,
      });

      const participants = data.stats?.participants;
      const progress = data.stats?.progress;
      const eligible = participants?.eligible_count || 0;
      const isActiveMission = data.status === MISSION_STATUS.ACTIVE.value;
      const isExpiredMission = data.status === MISSION_STATUS.EXPIRED.value;
      const percentAccumulated = ((isActiveMission || isExpiredMission) && data.mission_goal === MISSION_GOAL.HIGHEST_SCORE.value && Number(progress?.total_achieved) > 0) ? 100 : Number(progress?.goal_progress_percent) ?? 0;

      setProgressData({
        mission_code: data.code,
        status: data.status,
        status_label: data.status_label_th ?? data.status,
        mission_goal: data.mission_goal,
        category: data.category,
        target_amount: progress?.target_amount ?? data.target_amount,
        target_unit: getTargetUnitLabel(data.category),
        accumulated_total: progress?.total_achieved ?? 0,
        accumulated_percent: percentAccumulated,
        joined_count: participants?.joined_count ?? 0,
        joined_percent: calculatePercent(participants?.joined_count ?? 0, eligible),
        pending_count: participants?.not_joined_count ?? 0,
        pending_percent: calculatePercent(participants?.not_joined_count ?? 0, eligible),
        active_count: participants?.active_count ?? 0,
        active_percent: calculatePercent(participants?.active_count ?? 0, participants?.joined_count),
      });
    } else {
      setInitialValues(null);
      setProgressData(null);
    }
    setIsLoading(false);
  }, [id, companyOptions, loadingMasterData]);


  const buildFormData = useCallback((formValues, status) => {
    const goal = formValues.mission_goal?.value;
    const category = formValues.category?.value;
    const isTeam = formValues.challenge_type?.value === "team";
    const isPending = status === MISSION_STATUS.PENDING.value;
    const isRegistration = status === MISSION_STATUS.REGISTRATION.value;
    const isActive = status === MISSION_STATUS.ACTIVE.value;
    const fd = new FormData();
    const thFile = formValues.image_file_th?.find((f) => f.isNewUpload && !f.isDelete);
    const enFile = formValues.image_file_en?.find((f) => f.isNewUpload && !f.isDelete);
    if (thFile) fd.append("image_file_th", thFile);
    if (enFile) fd.append("image_file_en", enFile);
    if (isActive) {
      if (formValues.end_date) {
        fd.append("end_date", moment(formValues.end_date).format("YYYY-MM-DD"));
      }
      return fd;
    }

    if (isRegistration) {
      if (isTeam && formValues.team_count) {
        fd.append("team_count", formValues.team_count);
      }
      if (formValues.end_date) {
        fd.append("end_date", moment(formValues.end_date).format("YYYY-MM-DD"));
      }
      return fd;
    }

    (formValues.company_ids || []).forEach((c) =>
      fd.append("company_ids[]", c.value)
    );

    fd.append("name_th", formValues.name_th);
    fd.append("name_en", formValues.name_en);
    fd.append("mission_goal", goal);

    if (!status) {
      fd.append("category", category);
      fd.append("challenge_type", formValues.challenge_type?.value);
      if (formValues.begin_date) {
        fd.append("begin_date", moment(formValues.begin_date).format("YYYY-MM-DD"));
      }
    }

    if (formValues.end_date) {
      fd.append("end_date", moment(formValues.end_date).format("YYYY-MM-DD"));
    }
    if (formValues.target_amount) {
      fd.append("target_amount", formValues.target_amount);
    }

    if (isTeam) {
      if (formValues.team_count) { fd.append("team_count", formValues.team_count) }
      if (!isPending && formValues.team_size) { fd.append("team_size", formValues.team_size) }
      if (!isPending && formValues.registration_start_date) {
        fd.append("registration_date", moment(formValues.registration_start_date).format("YYYY-MM-DD"));
      }
    }

    const rankCoins = [];

    if (
      goal === MISSION_GOAL.SHARED_GOAL.value ||
      goal === MISSION_GOAL.FIRST_COMPLETE.value
    ) {
      if (formValues.success_coin_amount) {
        rankCoins.push({ reward_type: "completion", reward_rank: 1, coin_amount: Number(formValues.success_coin_amount) });
      }
      if (formValues.fail_coin_amount) {
        rankCoins.push({ reward_type: "completion", reward_rank: 2, coin_amount: Number(formValues.fail_coin_amount) });
      }
    } else if (goal === MISSION_GOAL.HIGHEST_SCORE.value) {
      const ranks = [
        { amount: formValues.reward_1st_amount, rank: 1 },
        { amount: formValues.reward_2nd_amount, rank: 2 },
        { amount: formValues.reward_3rd_amount, rank: 3 },
        { amount: formValues.reward_consolation_amount, rank: 4 },
      ];
      ranks.forEach(({ amount, rank }) => {
        if (amount) {
          rankCoins.push({ reward_type: "leaderboard", reward_rank: rank, coin_amount: Number(amount) });
        }
      });
    }

    rankCoins.forEach((coin, idx) => {
      fd.append(`rank_coins[${idx}][reward_type]`, coin.reward_type);
      fd.append(`rank_coins[${idx}][reward_rank]`, coin.reward_rank);
      fd.append(`rank_coins[${idx}][coin_amount]`, coin.coin_amount);
    });


    return fd;
  }, []);

  const onSubmit = useCallback(
    async (formValues) => {
      setIsBgLoading(true);
      const currentStatus = isCreate ? null : progressData?.status;
      const fd = buildFormData(formValues, currentStatus);
      const res = isCreate ? await adminService.POST_CREATE_MISSION_V2(fd)
        : await adminService.PATCH_UPDATE_MISSION_V2(id, fd);
      if (res?.success) {
        toast.success(
          <Displays.MsgToast
            theme_success_toast
            labelTop={isCreate ? "บันทึกสำเร็จ" : "แก้ไขสำเร็จ"}
          />
        );
        handleBack();
        setIsBgLoading(false);
      } else {
        toast.error(
          <Displays.MsgToast
            theme_error_toast
            labelTop={isCreate ? "บันทึกไม่สำเร็จ" : "แก้ไขไม่สำเร็จ"}
            labelBottom={'มีข้อผิดพลาดบางอย่าง กรุณาลองใหม่อีกครั้ง'}
          />
        );
      }
      setIsBgLoading(false);
    },
    [buildFormData, handleBack, isCreate, id, progressData]
  );

  const handleOpenModal = useCallback(() => setIsShowModal(true), []);
  const handleCloseModal = useCallback(() => setIsShowModal(false), []);

  const handleDelete = useCallback(async () => {
    setIsShowModal(false);
    setIsBgLoading(true);
    const res = await adminService.DELETE_MISSION_V2(id);
    if (res?.success) {
      toast.success(<Displays.MsgToast theme_success_toast labelTop="ลบสำเร็จ" />);
      handleBack();
    } else {
      toast.error(
        <Displays.MsgToast
          theme_error_toast
          labelTop="ลบไม่สำเร็จ"
          labelBottom="มีข้อผิดพลาดบางอย่าง กรุณาลองใหม่อีกครั้ง"
        />
      );
    }
    setIsBgLoading(false);
  }, [handleBack, id]);


  useEffect(() => {
    if (!isCreate) {
      fetchData();
    }
  }, [isCreate, fetchData]);

  return (
    <AddEditCompanyMissionV2ContainerStyled>
      <div className="content_section">
        {isLoading ? (
          <div className="loading_wrap">
            <Loading theme_standard_loading />
          </div>
        ) : (
          <Forms.AddCompanyMissionV2Form
            initialValues={initialValues}
            isCreate={isCreate}
            onSubmit={onSubmit}
            onBack={handleBack}
            onDeleteClick={handleOpenModal}
            progressData={progressData}
            onTeamManagementClick={handleTeamManagement}
            onLeaderboardClick={handleLeaderboard}
            companyOptions={companyOptions}
          />
        )}
      </div>

      <Modal
        theme_modal_standard
        isShow={isShowModal}
        handleClickCloseModal={handleCloseModal}
      >
        <BoxDeleteConfirm
          handleCancel={handleCloseModal}
          handleConfirm={handleDelete}
          iconPrefixTitle={<Icons.Delete5 width="32" height="32" />}
          textShow="ยืนยันการลบภารกิจนี้หรือไม่ ?"
          textBottom={`คุณต้องการลบภารกิจ\n"${initialValues?.name_th || ""}"`}
        />
      </Modal>

      <BGLoading visible={isBgLoading} />
    </AddEditCompanyMissionV2ContainerStyled>
  );
};

export default AddEditCompanyMissionV2Container;
